import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Check, ChevronDown } from "lucide-react";
import { flushWaitlistRetryQueue, submitWaitlistWithRetry } from "../lib/api";
import LiveLeaderboard from "../components/fyllr/LiveLeaderboard";
import { motion } from "framer-motion";
import { EDITORIAL_IMAGES, SERVICE_IMAGES } from "../lib/editorialImages";

const HERO_IMG = EDITORIAL_IMAGES.hero;
const HERO_ROTATOR = ["soon", "this summer", "for you", "in Melbourne"];
const EXPERIENCE_CARDS = [
  { label: "Wellness", sublabel: "Feel better", image: SERVICE_IMAGES.featureWellness, alt: "Quiet luxury in a travertine bathhouse" },
  { label: "Grooming", sublabel: "Look sharp", image: SERVICE_IMAGES.featureGrooming, alt: "Precision taper at the barbershop" },
  { label: "Beauty", sublabel: "Radiate now", image: SERVICE_IMAGES.featureBeauty, alt: "Luxury facial serum treatment" },
];

export default function Home() {
  const [rotatorIdx, setRotatorIdx] = useState(0);
  const [rotatorPhase, setRotatorPhase] = useState("in");

  useEffect(() => {
    flushWaitlistRetryQueue();
    const retry = () => flushWaitlistRetryQueue();
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  }, []);

  const scrollToSignup = (e) => {
    if (e) e.preventDefault();
    document.getElementById("signup")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div>
      {/* ========= EDITORIAL LANDING HERO ========= */}
      <section className="relative min-h-[92svh] md:min-h-[780px] bg-ink text-bone overflow-hidden" data-testid="hero-immersive">
        <img src={HERO_IMG} alt="Woman in a sculptural thermal pool bathhouse" className="fyllr-kenburns absolute inset-0 w-full h-full object-cover opacity-90 saturate-[0.76] contrast-[1.03]" data-testid="hero-bathhouse-image" />
        <div className="absolute inset-0 bg-[#1F0D0A]/24 mix-blend-multiply pointer-events-none" />
        <div className="absolute inset-y-0 left-0 w-full md:w-[62%] bg-gradient-to-r from-[#1F0D0A]/68 via-[#1F0D0A]/34 to-transparent pointer-events-none" />
        <div className="fyllr-hero-grain pointer-events-none" />

        <div className="relative z-10 min-h-[92svh] md:min-h-[780px] flex flex-col justify-end px-6 md:px-12 lg:px-16 pb-14 md:pb-20">
          <motion.div
            className="max-w-4xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          >
            <h1 className="max-w-none font-serif font-light tracking-tight text-bone text-[3.6rem] max-[390px]:text-[2.55rem] max-[375px]:text-[2.3rem] sm:text-[5rem] lg:text-[clamp(5.5rem,6.2vw,7.4rem)] leading-[0.88]" data-testid="hero-headline">
              <span className="whitespace-nowrap">Good plans</span>
              <br />
              <span className="whitespace-nowrap">happen last minute.</span>
            </h1>
            <p className="mt-5 max-w-md text-[17px] md:text-xl font-serif text-bone/90 leading-snug" data-testid="hero-prelaunch-message">
              Melbourne, we’re coming. 07.02.27.
            </p>
            <p className="mt-4 max-w-md text-[13px] md:text-[15px] text-bone/72 leading-relaxed">
              Join early access from 7 January to preview FYLLR before public booking requests open in February.
            </p>
            <div className="mt-9 max-w-xl">
              <a href="#signup" onClick={scrollToSignup} className="group inline-flex items-center gap-2 bg-burgundy px-8 py-4 text-[12px] tracking-[0.12em] text-bone transition-colors duration-300 hover:bg-espresso" data-testid="hero-early-access-cta">
                Get early access <ArrowRight size={15} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <div className="mt-6">
                <a href="#signup" onClick={scrollToSignup} className="text-[10px] tracking-[0.22em] uppercase text-bone/70 hover:text-bone border-b border-bone/35 hover:border-bone pb-1 transition-colors" data-testid="hero-first-to-know-link">
                  Be the first to know →
                </a>
              </div>
            </div>
          </motion.div>
        </div>
        <aside className="absolute right-6 top-1/2 z-10 hidden -translate-y-1/2 border-l border-bone/30 pl-5 text-[10px] tracking-[0.22em] text-bone/80 md:block" data-testid="hero-category-list">
          {['Spas', 'Bathhouses', 'Saunas', 'Massage', 'Skincare', 'Grooming', 'Beauty'].map((category) => (
            <p key={category} className="py-1.5 uppercase">{category}</p>
          ))}
        </aside>
      </section>

      {/* ========= BEAUTY / GROOMING / WELLNESS EDIT ========= */}
      <section className="bg-[#180B09] text-bone px-6 py-28 md:px-16 md:py-40" data-testid="editorial-interlude">
        <div className="mx-auto grid max-w-[1500px] gap-14 lg:grid-cols-[0.86fr_2fr] lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 34 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-bone/55">Same day, a better you</p>
            <h2 className="mt-7 font-serif text-5xl leading-[0.88] md:text-7xl lg:text-8xl" data-testid="experience-heading">
              Beauty.<br />Grooming.<br />Wellness.
            </h2>
            <p className="mt-8 max-w-sm text-[15px] leading-relaxed text-bone/68">
              From glow-ups to deep resets, FYLLR gives you instant access to Melbourne’s best beauty, grooming and wellness experiences — when you need them most.
            </p>
            <a href="#signup" onClick={scrollToSignup} className="mt-10 inline-flex border-b border-bone/45 pb-1 text-[10px] uppercase tracking-[0.2em] text-bone transition-colors hover:border-bone/70 hover:text-bone/70" data-testid="experience-marketplace-link">Get early access →</a>
          </motion.div>
          <div className="grid gap-px overflow-hidden bg-bone/15 sm:grid-cols-3" data-testid="experience-card-grid">
            {EXPERIENCE_CARDS.map((card, index) => (
              <motion.article
                key={card.label}
                className="group relative min-h-[400px] overflow-hidden bg-ink sm:min-h-[520px]"
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`experience-card-${card.label.toLowerCase()}`}
              >
                <img src={card.image} alt={card.alt} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
                <div className="absolute inset-x-0 bottom-0 h-[42%] bg-gradient-to-t from-[#1F0D0A]/72 via-[#1F0D0A]/22 to-transparent pointer-events-none" />
                <div className="absolute inset-x-0 bottom-0 px-5 py-5">
                  <p className="font-serif text-3xl">{card.label}</p>
                  <p className="mt-1 text-[9px] uppercase tracking-[0.22em] text-bone/60">{card.sublabel}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ========= HOW IT WORKS ========= */}
      <HowItWorks />

      {/* ========= LAUNCH COUNTDOWN + LIVE LEADERBOARD ========= */}
      <LiveLeaderboard />

      {/* ========= CUSTOMER WAITLIST ========= */}
      <CustomerWaitlist />

      {/* ========= BUSINESS WAITLIST ========= */}
      <BusinessWaitlist />
    </div>
  );
}


/* ---- How it works (editorial, scroll motion) ---- */
function HowItWorks() {
  const steps = [
    { n: "01", t: "Discover", d: "Explore the best beauty, grooming and wellness spaces near you.", image: EDITORIAL_IMAGES.architecture },
    { n: "02", t: "Choose", d: "Find an available time that works for right now.", image: SERVICE_IMAGES.nails },
    { n: "03", t: "Book", d: "Instant confirmation. Less planning, more you.", image: SERVICE_IMAGES.lashes },
  ];
  return (
    <section id="how" className="bg-bone text-ink px-6 py-28 md:px-16 md:py-40 overflow-hidden" data-testid="how-it-works">
      <div className="mx-auto grid max-w-[1500px] gap-14 lg:grid-cols-[1fr_1.65fr] lg:gap-20">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-xl"
      >
        <p className="text-[10px] tracking-[0.3em] uppercase text-burgundy mb-6">How it works</p>
        <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.92]">
          Discover.<br />Choose.<br />Book.
        </h2>
        <p className="mt-7 max-w-sm text-[15px] leading-relaxed text-ink/65">Last-minute beauty, grooming and wellness in three simple steps.</p>
        <a href="#signup" className="mt-9 inline-flex border-b border-espresso/40 pb-1 text-[10px] uppercase tracking-[0.2em] text-espresso transition-colors hover:border-espresso" data-testid="how-marketplace-link">Get early access →</a>
      </motion.div>
      <div className="grid gap-3 sm:grid-cols-3">
        {steps.map((s, i) => (
          <motion.div
            key={s.n}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="group relative min-h-[360px] overflow-hidden bg-ink text-bone sm:min-h-[460px]"
          >
            <img src={s.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-[1.04]" />
            <div className="absolute inset-0 bg-[#1F0D0A]/38" />
            <div className="absolute inset-x-0 bottom-0 border-t border-bone/20 bg-[#1F0D0A]/54 p-5">
              <span className="font-serif text-4xl text-bone/72">{s.n}</span>
              <h3 className="mt-6 font-serif text-3xl">{s.t}</h3>
              <p className="mt-3 text-[13px] leading-relaxed text-bone/72">{s.d}</p>
            </div>
          </motion.div>
        ))}
      </div>
      </div>
    </section>
  );
}

/* ---- Customer waitlist (email-first, personalise after) ---- */
function CustomerWaitlist() {
  return (
    <section id="signup" className="bg-bone text-ink px-6 md:px-16 py-24 md:py-32 border-t border-sand/50" data-testid="customer-waitlist">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-24 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-[10px] tracking-[0.3em] uppercase text-burgundy mb-6">The waitlist</p>
          <h2 className="font-serif font-light text-5xl md:text-7xl leading-[1.0]">Private <em className="italic text-burgundy">access.</em></h2>
          <p className="mt-6 max-w-sm text-[15px] md:text-lg text-ink/65 leading-relaxed">
            Receive the curated release of Melbourne openings, before they are gone.
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:pt-10"
        >
          <HeroWaitlist variant="signup" />
        </motion.div>
      </div>
    </section>
  );
}

/* ---- Dark field helper ---- */
function Field({ label, value, onChange, type = "text", required = false, placeholder = "", testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.22em] uppercase text-bone/50 mb-2">{label}</span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-bone/30 text-bone text-[15px] pb-3 outline-none focus:border-bone placeholder:text-bone/30 transition-colors"
        data-testid={testid}
      />
    </label>
  );
}

/* ---- Business waitlist (two-step, refined) ---- */
function BusinessWaitlist() {
  const [step, setStep] = useState(1);
  const [bizName, setBizName] = useState("");
  const [email, setEmail] = useState("");
  const [suburb, setSuburb] = useState("");
  const [contact, setContact] = useState("");
  const [phone, setPhone] = useState("");
  const [link, setLink] = useState("");
  const [categories, setCategories] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const toggle = (v) => setCategories((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]));

  const reveal = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  };

  const saveStep1 = async (e) => {
    e.preventDefault();
    if (!bizName.trim() || !email.trim()) return;
    setSaving(true); setError("");
    try {
      await submitWaitlistWithRetry({ email: email.trim().toLowerCase(), role: "salon", salon_name: bizName.trim(), suburb: suburb.trim() || undefined, source: "website-business" });
      setStep(2);
    } catch (err) {
      if (err?.response?.status === 409 || err?.response?.status === 200) setStep(2);
      else setError("Something went wrong — please try again.");
    } finally { setSaving(false); }
  };

  const saveStep2 = async (skip) => {
    if (!skip) {
      setSaving(true);
      try {
        await submitWaitlistWithRetry({ email: email.trim().toLowerCase(), role: "salon", salon_name: bizName.trim(), suburb: suburb.trim() || undefined, phone: phone.trim() || undefined, contact_name: contact.trim() || undefined, website: link.trim() || undefined, preferred_categories: categories, availability_types: availability, source: "website-business" });
      } catch (err) { console.warn("Optional business waitlist details could not be saved.", err); } finally { setSaving(false); }
    }
    setDone(true);
  };

  return (
    <section id="business" className="bg-espresso text-bone px-6 md:px-16 py-28 md:py-36 overflow-hidden" data-testid="business-waitlist">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 lg:gap-24 items-start">
        <motion.div {...reveal}>
          <p className="text-[10px] tracking-[0.3em] uppercase text-mocha mb-6">For partners</p>
          <h2 className="font-serif font-light text-5xl md:text-7xl leading-[1.0]">
            Fill your gaps,
            <br />
            <em className="italic text-mocha">beautifully.</em>
          </h2>
          <p className="mt-6 max-w-sm text-[15px] md:text-lg text-bone/65 leading-relaxed">
            Last-minute cancellations and quiet spots? FYLLR helps beauty and wellness businesses turn available appointments into new opportunities.
          </p>
          <div className="mt-10 hidden lg:block">
            <Link to="/salon/login" className="text-[11px] tracking-[0.22em] uppercase text-bone/55 hover:text-bone border-b border-bone/30 pb-1 transition-colors" data-testid="biz-login-link">
              Already a partner? Sign in →
            </Link>
          </div>
        </motion.div>

        <motion.div {...reveal} className="lg:pt-6">
          {done ? (
            <div data-testid="biz-done">
              <p className="font-serif italic text-4xl md:text-5xl">Thank you.</p>
              <p className="mt-3 text-bone/70 text-[15px] max-w-sm leading-relaxed">We've received your interest and will reach out within 48 hours to get you set up.</p>
            </div>
          ) : step === 1 ? (
            <form onSubmit={saveStep1} data-testid="biz-step1">
              <p className="text-[10px] tracking-[0.3em] uppercase text-bone/50 mb-6">Step 1 · Register interest</p>
              <div className="space-y-7">
                <Field label="Business name" value={bizName} onChange={setBizName} required placeholder="Studio Neue / The Bathhouse" testid="biz-name" />
                <Field label="Email" type="email" value={email} onChange={setEmail} required placeholder="owner@studio.com" testid="biz-email" />
                <Field label="Suburb" value={suburb} onChange={setSuburb} placeholder="Fitzroy, Prahran, CBD…" testid="biz-suburb" />
              </div>
              {error && <p className="mt-4 text-red-300 text-sm" data-testid="biz-error">{error}</p>}
              <button type="submit" disabled={saving} className="mt-9 inline-flex items-center justify-center gap-2 bg-bone text-espresso px-9 py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-espresso hover:text-bone transition-colors disabled:opacity-60" data-testid="biz-step1-submit">
                {saving ? "Saving…" : "Register interest"} <ArrowRight size={14} strokeWidth={1.5} />
              </button>
              <p className="mt-4 text-[11px] text-bone/40 italic">We save your enquiry immediately — the rest is optional.</p>
            </form>
          ) : (
            <div data-testid="biz-step2">
              <p className="text-[10px] tracking-[0.3em] uppercase text-mocha mb-2">You're on the list, {bizName}.</p>
              <p className="font-serif italic text-3xl md:text-4xl mb-7">A few optional details.</p>
              <div className="space-y-7">
                <Field label="Contact name" value={contact} onChange={setContact} placeholder="Your name" testid="biz-contact" />
                <Field label="Phone" value={phone} onChange={setPhone} placeholder="04xx xxx xxx" testid="biz-phone" />
                <Field label="Website or Instagram" value={link} onChange={setLink} placeholder="@yourstudio" testid="biz-link" />
              </div>
              <div className="mt-8">
                <p className="text-[10px] tracking-[0.22em] uppercase text-bone/50 mb-4">What do you offer?</p>
                <SelectionList>
                  {BIZ_CATEGORIES.map((c) => (
                    <EditorialChoice key={c} label={c} active={categories.includes(c)} onClick={() => toggle(c)} testId={`biz-cat-${c.toLowerCase().replace(/[\s+]+/g, "-")}`} />
                  ))}
                </SelectionList>
              </div>
              <div className="mt-8">
                <p className="text-[10px] tracking-[0.22em] uppercase text-bone/50 mb-4">Availability you'd list</p>
                <SelectionList>
                  {BIZ_AVAILABILITY.map((a) => (
                    <EditorialChoice key={a} label={a} active={availability.includes(a)} onClick={() => setAvailability((p) => (p.includes(a) ? p.filter((x) => x !== a) : [...p, a]))} testId={`biz-avail-${a.toLowerCase().replace(/\s/g, "-")}`} />
                  ))}
                </SelectionList>
              </div>
              <div className="mt-10 flex items-center gap-7">
                <button onClick={() => saveStep2(false)} disabled={saving} className="inline-flex items-center gap-2 bg-bone text-espresso px-9 py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-espresso hover:text-bone transition-colors disabled:opacity-60" data-testid="biz-step2-save">
                  {saving ? "Saving…" : "Save details"} <ArrowRight size={14} strokeWidth={1.5} />
                </button>
                <button onClick={() => saveStep2(true)} className="text-[11px] tracking-[0.22em] uppercase text-bone/55 hover:text-bone transition-colors" data-testid="biz-step2-skip">Skip</button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}

/* ---- Hero quick-join: email only, personalise later (simplified) ---- */
function HeroQuickJoin() {
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const goPrefs = (e) => {
    if (e) e.preventDefault();
    window.dispatchEvent(new Event("fyllr:refine"));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true); setErr("");
    try {
      await submitWaitlistWithRetry({ email: email.trim().toLowerCase(), role: "client", email_opt_in: true, source: "website" });
      setDone(true);
    } catch (e2) {
      if (e2?.response?.status === 409 || e2?.response?.status === 200) setDone(true);
      else setErr("Something went wrong — please try again.");
    } finally { setSaving(false); }
  };

  if (done) {
    return (
      <div data-testid="hero-join-done">
        <p className="font-serif italic text-bone text-4xl md:text-5xl">You're in.</p>
        <p className="mt-3 text-bone/70 text-[14px] max-w-sm leading-relaxed">
          We’ll share your early-access invitation on 7 January.{" "}
          <a href="#signup" onClick={goPrefs} className="text-mocha border-b border-mocha/50 hover:border-mocha transition-colors" data-testid="hero-join-prefs">
            Tell us your taste →
          </a>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="hero-join-form">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          required
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
          placeholder="Your email address"
          aria-label="Your email address"
          className="flex-1 bg-transparent border-b border-bone/40 focus:border-bone text-bone text-[15px] py-3 outline-none placeholder:text-bone/40 transition-colors"
          data-testid="hero-join-email"
        />
        <button
          type="submit"
          disabled={saving}
          className={`group shrink-0 inline-flex items-center justify-center gap-2 bg-burgundy text-bone px-8 py-4 text-[12px] tracking-[0.12em] rounded-[2px] transition-colors duration-300 hover:bg-espresso ${saving ? "opacity-60" : ""}`}
          data-testid="hero-join-submit"
        >
          {saving ? "Joining…" : "JOIN THE WAITLIST"}
          {!saving && <ArrowRight size={15} strokeWidth={2} className="group-hover:translate-x-1 transition-transform" />}
        </button>
      </div>
      {err && <p className="mt-3 text-red-300 text-sm" data-testid="hero-join-error">{err}</p>}
    </form>
  );
}

/* ---- Hero editorial text column (logo, headline, email capture) ---- */
function HeroText({ rotatorIdx, rotatorPhase, mobile = false }) {
  const v = mobile ? "mobile" : "desktop";
  const headlineWords = ["Last-minute", "beauty &", "wellness,"];
  return (
    <div className={mobile ? "max-w-xl" : "max-w-md xl:max-w-lg"}>
      {/* Logo lockup */}
      <div data-testid={`hero-logo-lockup-${v}`}>
        <img src="/brand/fyllr-wordmark-oxblood.png" alt="FYLLR" className="h-7 md:h-8 w-auto" data-testid={`hero-logo-image-${v}`} />
        <p className="mt-2.5 text-[9px] tracking-[0.42em] uppercase text-sand">Beauty &amp; Wellness</p>
      </div>

      {/* Coming soon label */}
      <div className="mt-8 inline-flex items-center gap-2">
        <span className="relative flex h-1.5 w-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-burgundy/60 opacity-75" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-burgundy" />
        </span>
        <span className="text-[10px] tracking-[0.24em] uppercase text-burgundy">Coming soon · Melbourne</span>
      </div>

      {/* Headline */}
      <h1 className="mt-5 font-serif tracking-tight text-ink text-[2.6rem] sm:text-5xl lg:text-[3.5rem] xl:text-[3.9rem] leading-[1.05]">
        {headlineWords.map((w) => (
          <span key={w} className="inline-block mr-[0.26em]">{w}</span>
        ))}
        <br />
        <span className="inline-block">coming</span>{" "}
        <span className="relative inline-block align-baseline">
          <span
            key={rotatorIdx + rotatorPhase}
            className={`italic text-espresso ${rotatorPhase === "in" ? "fyllr-rotator-in" : "fyllr-rotator-out"}`}
            data-testid={`hero-rotator-${v}`}
          >
            {HERO_ROTATOR[rotatorIdx]}
          </span>
          <span className="absolute -right-1 bottom-1.5 w-[3px] h-[0.6em] bg-espresso/50 animate-pulse" aria-hidden="true" />
        </span>
      </h1>

      <p className="mt-6 text-[15px] md:text-base text-ink/70 max-w-sm leading-relaxed">
        Premium last-minute openings at Melbourne's best salons, spas and wellness studios. Join the list for first access.
      </p>

      {/* Email capture */}
      <div className="mt-8">
        <HeroWaitlist variant={v} />
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-2 rounded-full bg-espresso text-bone px-5 py-2.5 text-[13px] hover:bg-ink transition-colors"
            data-testid={`hero-marketplace-link-${v}`}
          >
            Browse last-minute openings <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
          <Link
            to="/sessions"
            className="inline-flex items-center gap-2 text-burgundy text-[13px] hover:text-espresso transition-colors"
            data-testid={`hero-sessions-link-${v}`}
          >
            Browse sessions <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ---- Hero email-first waitlist capture (immediately visible) ---- */
function HeroWaitlist({ variant = "desktop" }) {
  const [email, setEmail] = useState("");
  const [open, setOpen] = useState(false);
  const [suburbs, setSuburbs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [days, setDays] = useState([]);
  const [times, setTimes] = useState([]);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState("");

  const toggle = (arr, setArr, val) =>
    setArr((prev) => (prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]));

  const formRef = useRef(null);
  useEffect(() => {
    if (variant !== "signup") return;
    const handler = () => {
      setOpen(true);
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
    };
    window.addEventListener("fyllr:refine", handler);
    return () => window.removeEventListener("fyllr:refine", handler);
  }, [variant]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    setError("");
    try {
      const result = await submitWaitlistWithRetry({
        email: email.trim().toLowerCase(),
        role: "client",
        preferred_suburbs: suburbs,
        preferred_categories: categories,
        preferred_days: days,
        preferred_times: times,
        email_opt_in: true,
        sms_opt_in: smsOptIn,
        phone: smsOptIn ? phone.trim() || undefined : undefined,
        source: "website",
      });
      setQueued(result.queued);
      setDone(true);
    } catch (err) {
      if (err?.response?.status === 200 || err?.response?.status === 409) {
        setDone(true);
      } else {
        setError("Something went wrong — please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-md" data-testid={`hero-signup-done-${variant}`}>
        <p className="text-[10px] tracking-[0.26em] uppercase text-burgundy mb-2">You're on the list</p>
        <p className="font-serif text-2xl md:text-3xl text-ink">Welcome to FYLLR.</p>
        <p className="mt-2 text-[13px] text-ink/65">
          {queued
            ? "Safely queued — it'll send the moment you reconnect."
            : "We’ll share your early-access invitation on 7 January 2027."}
        </p>
      </div>
    );
  }

  return (
    <form ref={formRef} id={variant === "signup" ? "preferences" : undefined} onSubmit={onSubmit} className="max-w-md" data-testid={`hero-waitlist-form-${variant}`}>
      {/* Email + submit */}
      <div className="flex items-center gap-3 border-b border-ink/25 focus-within:border-espresso transition-colors">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email address"
          className="flex-1 bg-transparent text-ink text-[15px] py-3 outline-none placeholder:text-ink/40"
          data-testid={`email-input-${variant}`}
        />
        <button
          type="submit"
          disabled={saving}
          aria-label="Join the waitlist"
          className={`shrink-0 w-11 h-11 rounded-full bg-espresso inline-flex items-center justify-center text-bone hover:bg-ink transition-colors ${saving ? "opacity-60" : ""}`}
          data-testid={`submit-btn-${variant}`}
        >
          <ArrowRight size={16} strokeWidth={1.5} />
        </button>
      </div>

      {/* Refine toggle */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-burgundy hover:text-espresso transition-colors"
        aria-expanded={open}
        data-testid={`hero-refine-toggle-${variant}`}
      >
        Refine my taste (optional)
        <ChevronDown size={13} strokeWidth={1.5} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Expandable preferences */}
      {open && (
        <div
          className="mt-5 text-left bg-bone border border-sand/50 p-5 space-y-6 fyllr-card-in"
          data-testid="hero-refine-panel"
        >
          <div>
            <p className="text-[10px] tracking-[0.22em] uppercase text-sand mb-3">Preferred suburbs</p>
            <SelectionList compact>
              {SUBURBS.map((s) => (
                <EditorialChoice light key={s} label={s} active={suburbs.includes(s)} onClick={() => toggle(suburbs, setSuburbs, s)} testId={`hero-pref-suburb-${s.toLowerCase().replace(/\s/g, "-")}`} />
              ))}
            </SelectionList>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.22em] uppercase text-sand mb-3">Favourite categories</p>
            <SelectionList compact>
              {PREF_CATEGORIES.map((c) => (
                <EditorialChoice light key={c} label={c} active={categories.includes(c)} onClick={() => toggle(categories, setCategories, c)} testId={`hero-pref-cat-${c.toLowerCase().replace(/[\s+]+/g, "-")}`} />
              ))}
            </SelectionList>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-6">
            <div>
              <p className="text-[10px] tracking-[0.22em] uppercase text-sand mb-3">Days that work</p>
              <SelectionList compact>
                {DAYS.map((d) => (
                  <EditorialChoice light key={d} label={d} active={days.includes(d)} onClick={() => toggle(days, setDays, d)} testId={`hero-pref-day-${d.toLowerCase()}`} />
                ))}
              </SelectionList>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.22em] uppercase text-sand mb-3">Time of day</p>
              <SelectionList compact>
                {TIMES.map((t) => (
                  <EditorialChoice light key={t} label={t} active={times.includes(t)} onClick={() => toggle(times, setTimes, t)} testId={`hero-pref-time-${t.toLowerCase()}`} />
                ))}
              </SelectionList>
            </div>
          </div>

          {/* Optional SMS alerts (separate consent) */}
          <div className="border-t border-sand/50 pt-5">
            <button type="button" onClick={() => setSmsOptIn((v) => !v)} className="flex items-center gap-3 text-left" data-testid="hero-sms-toggle">
              <span className={`w-4 h-4 shrink-0 border flex items-center justify-center transition-colors ${smsOptIn ? "bg-espresso border-espresso" : "border-ink/40"}`}>
                {smsOptIn && <Check size={11} strokeWidth={2.5} className="text-bone" />}
              </span>
              <span className="text-[13px] text-ink/80">Text me last-minute openings (optional SMS)</span>
            </button>
            {smsOptIn && (
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="04xx xxx xxx"
                className="mt-3 w-full bg-transparent border-b border-ink/25 text-ink py-2 outline-none focus:border-espresso text-[14px] placeholder:text-ink/35"
                data-testid="hero-sms-phone"
              />
            )}
            <p className="mt-2 text-[11px] text-ink/40 italic">Separate consent — message &amp; data rates may apply.</p>
          </div>
        </div>
      )}

      {error && <p className="mt-3 text-red-600 text-sm" data-testid="hero-signup-error">{error}</p>}
      <p className="mt-4 text-[11px] text-ink/45 italic">Private updates. Unsubscribe at any time.</p>
    </form>
  );
}



/* ---- Dual path: Client / Business signup ---- */

function DualSignup() {
  const [role, setRole] = useState("client"); // "client" | "business"

  return (
    <div className="grid lg:grid-cols-12 gap-y-12 lg:gap-x-16" data-testid="dual-signup">
      <div className="relative lg:col-span-5 min-h-[440px] sm:min-h-[520px] overflow-hidden" data-testid="signup-editorial-photo-panel">
        <img
          src={EDITORIAL_IMAGES.portraitAlt}
          alt="FYLLR editorial beauty portrait"
          className="fyllr-editorial-photo absolute inset-0 w-full h-full object-cover"
          data-testid="signup-editorial-photo"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-espresso/90 via-espresso/20 to-transparent" aria-hidden="true" />
        <div className="absolute inset-x-0 top-0 px-6 pt-7 md:px-9 md:pt-10">
          <p className="text-[10px] tracking-[0.26em] uppercase font-light text-bone/65">Get early access</p>
          <h2 className="mt-3 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.84] text-bone">
            Be first when<br />FYLLR launches.<br /><em className="text-bone/75">Melbourne.</em>
          </h2>
        </div>
      </div>

      <div className="lg:col-span-7 lg:pt-1">
      <div className="flex border-b border-bone/30" role="tablist" data-testid="signup-role-toggle">
        <button
          type="button"
          role="tab"
          aria-selected={role === "client"}
          onClick={() => setRole("client")}
          className={`relative -mb-px pb-3 mr-7 text-[11px] tracking-[0.16em] uppercase transition-colors ${
            role === "client" ? "text-bone border-b border-bone" : "text-bone/45 hover:text-bone"
          }`}
          data-testid="signup-role-client"
        >
          I'm looking to book
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={role === "business"}
          onClick={() => setRole("business")}
          className={`relative -mb-px pb-3 text-[11px] tracking-[0.16em] uppercase transition-colors ${
            role === "business" ? "text-bone border-b border-bone" : "text-bone/45 hover:text-bone"
          }`}
          data-testid="signup-role-business"
        >
          I'm a business
        </button>
      </div>

      <p className="mt-5 max-w-md text-[10px] leading-relaxed tracking-[0.14em] uppercase font-light text-bone/55">
        {role === "client"
          ? "Request private access to Melbourne's considered beauty and wellness edit."
          : "Register your salon, spa or wellness studio for the FYLLR partner edit."}
      </p>

      {/* Client signup */}
      {role === "client" && <ClientSignup />}

      {/* Business signup */}
      {role === "business" && <BusinessSignup />}
      </div>
    </div>
  );
}


/* ---- Inline signup + preferences form ---- */
const SUBURBS = ["Fitzroy", "Carlton", "Collingwood", "Richmond", "South Yarra", "Prahran", "Armadale", "St Kilda", "CBD"];
const PREF_CATEGORIES = ["Hair", "Skin", "Brows + Lashes", "Nails", "Massage", "Sauna", "Contrast Therapy", "Bathhouse", "Float Tank", "Infrared Sauna", "Body"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIMES = ["Morning", "Afternoon", "Evening"];

function ClientSignup() {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [suburbs, setSuburbs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [days, setDays] = useState([]);
  const [times, setTimes] = useState([]);
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [queued, setQueued] = useState(false);

  const toggle = (arr, setArr, val) =>
    setArr((prev) => (prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]));

  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    setError("");
    try {
      const result = await submitWaitlistWithRetry({
        email: email.trim().toLowerCase(),
        role: "client",
        preferred_suburbs: suburbs,
        preferred_categories: categories,
        preferred_days: days,
        preferred_times: times,
        email_opt_in: emailOptIn,
        sms_opt_in: smsOptIn,
        phone: smsOptIn ? phone.trim() : undefined,
        source: "website",
      });
      setQueued(result.queued);
      setDone(true);
    } catch (err) {
      // Duplicate email (already on the list) returns 200, so a real error means something broke
      if (err?.response?.status === 200 || err?.response?.status === 409) {
        setDone(true);
      } else {
        setError("Something went wrong — please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="py-8" data-testid="signup-done">
        <p className="text-[11px] tracking-[0.22em] uppercase text-bone/50 mb-3">You're on the list</p>
        <h2 className="font-serif text-3xl md:text-5xl leading-tight">Welcome to FYLLR.</h2>
        <p className="mt-3 text-bone/65 max-w-md mx-auto text-[15px]">
          {queued ? "Your signup is safely queued and will send when you reconnect." : "We'll be in touch when beauty & wellness openings go live in Melbourne."}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="signup-preferences-form">
      {/* Email */}
      <div className="mt-11 max-w-xl">
        <label className="block">
          <span className="block text-[10px] tracking-[0.22em] uppercase text-bone/50 mb-2">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@domain.com"
            className="w-full bg-transparent border-b border-bone/30 text-bone text-[15px] pb-3 outline-none focus:border-bone placeholder:text-bone/30"
            data-testid="signup-email"
          />
        </label>
      </div>

      {/* Preferences heading */}
      <div className="mt-14">
        <h3 className="font-serif text-3xl md:text-4xl leading-none text-bone">Your edit, your way.</h3>
        <p className="mt-3 max-w-lg text-[10px] leading-relaxed tracking-[0.14em] uppercase font-light text-bone/55">
          Tell us what you love and we'll match openings to your taste. Optional — skip anytime.
        </p>
      </div>

      {/* Preferred Suburbs */}
      <div className="mt-10">
        <p className="text-[10px] tracking-[0.22em] uppercase text-bone/50 mb-5">Preferred suburbs</p>
        <SelectionList>
          {SUBURBS.map((s) => (
            <EditorialChoice key={s} label={s} active={suburbs.includes(s)} onClick={() => toggle(suburbs, setSuburbs, s)} testId={`pref-suburb-${s.toLowerCase().replace(/\s/g, "-")}`} />
          ))}
        </SelectionList>
      </div>

      {/* Favourite Categories */}
      <div className="mt-10">
        <p className="text-[10px] tracking-[0.22em] uppercase text-bone/50 mb-5">Favourite categories</p>
        <SelectionList>
          {PREF_CATEGORIES.map((c) => (
            <EditorialChoice key={c} label={c} active={categories.includes(c)} onClick={() => toggle(categories, setCategories, c)} testId={`pref-cat-${c.toLowerCase().replace(/[\s+]+/g, "-")}`} />
          ))}
        </SelectionList>
      </div>

      {/* Days + Time of Day */}
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-10">
        <div>
          <p className="text-[10px] tracking-[0.22em] uppercase text-bone/50 mb-5">Days that work</p>
          <SelectionList compact>
            {DAYS.map((d) => (
              <EditorialChoice key={d} label={d} active={days.includes(d)} onClick={() => toggle(days, setDays, d)} testId={`pref-day-${d.toLowerCase()}`} />
            ))}
          </SelectionList>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.22em] uppercase text-bone/50 mb-5">Time of day</p>
          <SelectionList compact>
            {TIMES.map((t) => (
              <EditorialChoice key={t} label={t} active={times.includes(t)} onClick={() => toggle(times, setTimes, t)} testId={`pref-time-${t.toLowerCase()}`} />
            ))}
          </SelectionList>
        </div>
      </div>

      {/* Notification checkboxes */}
      <div className="mt-12 max-w-xl space-y-4 text-left">
        <label className="flex items-start gap-3 cursor-pointer group" data-testid="pref-email-optin">
          <span className={`mt-0.5 w-4 h-4 border flex items-center justify-center shrink-0 transition-colors ${emailOptIn ? "bg-bone border-bone" : "border-bone/40"}`}>
            {emailOptIn && <Check size={13} strokeWidth={2.5} className="text-espresso" />}
          </span>
          <span className="text-[15px] text-bone/80 group-hover:text-bone transition-colors">Email me a weekly curated edit</span>
          <input type="checkbox" className="sr-only" checked={emailOptIn} onChange={(e) => setEmailOptIn(e.target.checked)} />
        </label>
        <label className="flex items-start gap-3 cursor-pointer group" data-testid="pref-sms-optin">
          <span className={`mt-0.5 w-4 h-4 border flex items-center justify-center shrink-0 transition-colors ${smsOptIn ? "bg-bone border-bone" : "border-bone/40"}`}>
            {smsOptIn && <Check size={13} strokeWidth={2.5} className="text-espresso" />}
          </span>
          <span className="text-[15px] text-bone/80 group-hover:text-bone transition-colors">Text me on the day if something special pops up</span>
          <input type="checkbox" className="sr-only" checked={smsOptIn} onChange={(e) => setSmsOptIn(e.target.checked)} />
        </label>
        {smsOptIn && (
          <div className="pl-8">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="04XX XXX XXX"
              className="w-full max-w-xs bg-transparent border-b border-bone/30 text-bone text-[15px] pb-2 outline-none focus:border-bone/60 placeholder:text-bone/30"
              data-testid="signup-phone"
            />
          </div>
        )}
      </div>

      {/* Submit */}
      <div className="mt-12">
        <button
          type="submit"
          disabled={saving}
          className={`w-full inline-flex items-center justify-center gap-2 bg-bone text-espresso px-5 py-3.5 text-[11px] tracking-[0.2em] uppercase transition-colors ${saving ? "opacity-60" : "hover:bg-sand"}`}
          data-testid="signup-submit"
        >
          {saving ? "Joining…" : "Join the waitlist"} <ArrowRight size={14} strokeWidth={1.5} />
        </button>
        {error && <p className="mt-3 text-red-400 text-sm" data-testid="signup-error">{error}</p>}
        <p className="mt-4 text-[12px] text-bone/40 italic">
          By joining, you agree to receive FYLLR updates. Unsubscribe at any time.
        </p>
      </div>
    </form>
  );
}


const BIZ_CATEGORIES = ["Hair", "Skin", "Brows + Lashes", "Nails", "Massage", "Sauna", "Contrast Therapy", "Bathhouse", "Float Tank", "Infrared Sauna", "Body"];
const BIZ_AVAILABILITY = ["Cancellations", "Quiet days", "Last-minute slots", "Recurring gaps"];

function BusinessSignup() {
  const [email, setEmail] = useState("");
  const [bizName, setBizName] = useState("");
  const [suburb, setSuburb] = useState("");
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [queued, setQueued] = useState(false);

  const toggle = (val) =>
    setCategories((prev) => (prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]));

  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !bizName.trim()) return;
    setSaving(true);
    setError("");
    try {
      const result = await submitWaitlistWithRetry({
        email: email.trim().toLowerCase(),
        role: "salon",
        salon_name: bizName.trim(),
        suburb: suburb.trim() || undefined,
        preferred_categories: categories,
        source: "website",
      });
      setQueued(result.queued);
      setDone(true);
    } catch (err) {
      if (err?.response?.status === 200 || err?.response?.status === 409) {
        setDone(true);
      } else {
        setError("Something went wrong — please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="py-8" data-testid="biz-signup-done">
        <p className="text-[11px] tracking-[0.22em] uppercase text-bone/50 mb-3">You're on the list</p>
        <h3 className="font-serif text-3xl md:text-4xl">Welcome to FYLLR, {bizName}.</h3>
        <p className="mt-3 text-bone/65 max-w-md mx-auto text-[15px]">
          {queued ? "Your registration is safely queued and will send when you reconnect." : "We'll reach out when we're ready to onboard your business."}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="biz-signup-form">
      {/* Business details */}
      <div className="mt-11 max-w-xl space-y-7">
        <label className="block">
          <span className="block text-[10px] tracking-[0.22em] uppercase text-bone/50 mb-2">Business name</span>
          <input
            type="text"
            required
            value={bizName}
            onChange={(e) => setBizName(e.target.value)}
            placeholder="Studio Neue / The Bathhouse"
            className="w-full bg-transparent border-b border-bone/30 text-bone text-[15px] pb-3 outline-none focus:border-bone placeholder:text-bone/30"
            data-testid="biz-signup-name"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-[0.22em] uppercase text-bone/50 mb-2">Contact email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="owner@studio.com"
            className="w-full bg-transparent border-b border-bone/30 text-bone text-[15px] pb-3 outline-none focus:border-bone placeholder:text-bone/30"
            data-testid="biz-signup-email"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-[0.22em] uppercase text-bone/50 mb-2">Suburb</span>
          <input
            type="text"
            value={suburb}
            onChange={(e) => setSuburb(e.target.value)}
            placeholder="Fitzroy, Prahran, CBD…"
            className="w-full bg-transparent border-b border-bone/30 text-bone text-[15px] pb-3 outline-none focus:border-bone placeholder:text-bone/30"
            data-testid="biz-signup-suburb"
          />
        </label>
      </div>

      {/* Categories */}
      <div className="mt-10">
        <p className="text-[10px] tracking-[0.22em] uppercase text-bone/50 mb-5">What do you offer?</p>
        <SelectionList>
          {BIZ_CATEGORIES.map((c) => (
            <EditorialChoice key={c} label={c} active={categories.includes(c)} onClick={() => toggle(c)} testId={`biz-cat-${c.toLowerCase().replace(/[\s+]+/g, "-")}`} />
          ))}
        </SelectionList>
      </div>

      {/* Submit */}
      <div className="mt-12">
        <button
          type="submit"
          disabled={saving}
          className={`w-full inline-flex items-center justify-center gap-2 bg-bone text-espresso px-5 py-3.5 text-[11px] tracking-[0.2em] uppercase transition-colors ${saving ? "opacity-60" : "hover:bg-sand"}`}
          data-testid="biz-signup-submit"
        >
          {saving ? "Joining…" : "Register your business"} <ArrowRight size={14} strokeWidth={1.5} />
        </button>
        {error && <p className="mt-3 text-red-400 text-sm" data-testid="biz-signup-error">{error}</p>}
        <p className="mt-4 text-[12px] text-bone/40 italic">
          We'll review your application and get back within 48 hours.
        </p>
      </div>
    </form>
  );
}


function SelectionList({ children, compact = false }) {
  return <div className={`grid ${compact ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-2"} gap-x-8 gap-y-1`}>{children}</div>;
}

function EditorialChoice({ label, active, onClick, testId, light = false }) {
  const base = light
    ? active
      ? "text-espresso"
      : "text-ink/55 hover:text-espresso"
    : active
      ? "text-bone"
      : "text-bone/55 hover:text-bone";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-between py-2.5 text-left text-[13px] tracking-[0.04em] transition-colors ${base}`}
      data-testid={testId}
    >
      <span>{label}</span>
      <span className={`ml-4 font-serif text-lg leading-none transition-opacity ${active ? "opacity-100" : "opacity-0"}`} aria-hidden="true">✓</span>
    </button>
  );
}
